import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MercerData, PaginatedResult } from '@coin/shared/core/util-models';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { LoadingService } from '@coin/shared/core/data-access';
import { DirectsPositionClassComparisonService } from '../../services/directs-position-comparison.service';
import {
  MercerEvaluationMember,
  MercerEvaluationMemberDetails
} from '../../models/mercer-support-evaluation-member.model';
import { MercerDialogType } from '../../enums/mercer-dialog-type.enum';
import { DirectsPositionClassComparison } from '../../models/directs-position-class-comparison.model';
import { MercerSupportService } from '../../services/mercer-support.service';
import { MercerService } from '../../services/mercer.service';
import { MercerSupportDetailsService } from '../../services/mercer-support-details.service';

@Component({
  selector: 'coin-c-position-comparison-side-bar',
  templateUrl: './position-comparison-side-bar.component.html',
  styleUrls: ['./position-comparison-side-bar.component.scss'],
  standalone: false
})
export class PositionComparisonSideBarComponent implements OnInit, OnChanges {
  private mercerSupportService = inject(MercerSupportService);
  private mercerService = inject(MercerService);
  mercerSupportDetailsService = inject(MercerSupportDetailsService);
  private directsPositionClassComparisonService = inject(DirectsPositionClassComparisonService);
  private loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  @Input() public memberDetails: MercerEvaluationMemberDetails;
  @Input() public currentMercerData: MercerData;
  @Input() public seasonId: string;
  @Input() public dialogType: MercerDialogType;
  @Input() public isLocked: boolean;
  @Input() public hideCareerStreamAndLevel: boolean;

  @Output() public openPosition = new EventEmitter<MercerEvaluationMember>();

  public relatedPositions: Array<MercerEvaluationMember | DirectsPositionClassComparison>;
  public selectedOrgLevel: string;
  public selectedOrgCode: string;

  public MercerDialogType = MercerDialogType;

  public lazyLoadFn =
    (key: string) =>
    (search: string): Observable<string[]> => {
      if (this.dialogType === MercerDialogType.Support) {
        return this.mercerSupportService
          .getFieldValues(0, 50, key, search, this.seasonId)
          .pipe(map(response => response.content));
      }

      if (this.dialogType === MercerDialogType.Manager) {
        return this.mercerService
          .getFieldValues(0, 50, key, search, this.seasonId)
          .pipe(map(response => response.content));
      }
    };

  ngOnInit(): void {
    if (this.dialogType === MercerDialogType.Headcount) {
      this.getDirectsPositionClassComparison().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.memberDetails && changes.memberDetails?.previousValue?.id !== changes.memberDetails?.currentValue?.id) ||
      changes.memberDetails?.previousValue?.parentOrgCode !== changes.memberDetails?.currentValue?.parentOrgCode ||
      changes.memberDetails?.previousValue?.orgLevel !== changes.memberDetails?.currentValue?.orgLevel
    ) {
      this.setDefaultDropdownValues();
      this.updateRelatedPositions(changes.memberDetails.isFirstChange());
    }
  }

  private setDefaultDropdownValues(): void {
    this.selectedOrgCode = this.memberDetails?.parentOrgCode;
    this.selectedOrgLevel = this.memberDetails?.orgLevel?.toString();
  }

  public updateRelatedPositions(suppressLoadingScreen = false): void {
    const filterQueryOrgLevel = this.selectedOrgLevel ? `&Query.OrgLevel=${this.selectedOrgLevel}` : '';
    const filterQueryOrgCode = this.selectedOrgCode
      ? `&Query.OrgCodeStartsWith=${encodeURIComponent(this.selectedOrgCode)}`
      : '';
    const filterQuery = filterQueryOrgLevel + filterQueryOrgCode;
    let getMembers$: Observable<PaginatedResult<MercerEvaluationMember>>;

    switch (this.dialogType) {
      case MercerDialogType.Support:
        getMembers$ = this.mercerSupportService.getEvaluationMembers(this.seasonId, 0, 15, filterQuery);
        break;
      case MercerDialogType.Manager:
        getMembers$ = this.mercerService.getEvaluationMembers(this.seasonId, 0, 15, filterQuery);
        break;
      case MercerDialogType.Headcount:
        return;
    }

    getMembers$
      .pipe(
        this.loadingService.suppressLoadingScreen(suppressLoadingScreen),
        tap(result => {
          this.relatedPositions = result?.content?.filter(item => item.id !== this.memberDetails?.id);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getDirectsPositionClassComparison(): Observable<DirectsPositionClassComparison[]> {
    const positions$ = this.directsPositionClassComparisonService.getDirectsEvaluationMembers(
      this.memberDetails?.incumbentId,
      this.memberDetails?.orgCode
    );

    return positions$.pipe(
      tap(result => {
        this.relatedPositions = result?.filter(item => item.id !== this.memberDetails?.id);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }
}
